import type { Item } from "./IntersectionObserverManager";
import type { View } from "react-native";

import { useIntersectionObserverContext } from "./IntersectionObserverContext";

import { useLayoutEffect, useRef } from "react";

export interface UseIntersectionObserverItemOptions {
  onChange: ({
    isIntersecting,
    intersectionRatio,
  }: {
    isIntersecting: boolean;
    intersectionRatio: number;
  }) => void;
  triggerOnce?: boolean;
  threshold?:
    | {
        ratio: number;
      }
    | {
        size: number;
      };
}

export const useIntersectionObserverItem = ({
  onChange,
  triggerOnce = true,
  threshold = { ratio: 0 },
}: UseIntersectionObserverItemOptions) => {
  const viewRef = useRef<View>(null);
  const triggeredRef = useRef(false);

  const itemRef = useRef<Item>({
    viewRef,
    layout: {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    },
  });

  const { manager } = useIntersectionObserverContext();

  // useLayoutEffect to ensure the item is observed before the root's onContentSizeChange is called
  useLayoutEffect(() => {
    const currentItem = itemRef.current;
    if (manager) {
      // If triggerOnce is true and the item has been triggered, do not observe it again.
      // This prevents re-observing when dependencies (for example, the onChange function) of useLayoutEffect change.
      if (triggerOnce && triggeredRef.current) {
        return;
      }

      manager.observe(currentItem, ({ isIntersecting, intersectionRatio, intersectionSize }) => {
        // from not intersecting to intersecting
        if (isIntersecting) {
          const thresholdRatio = "ratio" in threshold ? threshold.ratio : 0;
          const thresholdSize = "size" in threshold ? threshold.size : 0;

          // Default to threshold ratio if size is not provided
          const passedThreshold =
            "size" in threshold
              ? intersectionSize >= thresholdSize
              : intersectionRatio >= thresholdRatio;

          if (passedThreshold) {
            onChange({ isIntersecting, intersectionRatio });
            if (triggerOnce) {
              manager.unobserve(currentItem);

              // Mark the item as triggered to prevent it from being observed again.
              triggeredRef.current = true;
            }
          }
        }
        // from intersecting to not intersecting
        else if (!isIntersecting) {
          onChange({ isIntersecting, intersectionRatio });
        }
      });
    }

    return () => {
      if (manager) {
        manager.unobserve(currentItem);
      }
    };
  }, [manager, onChange, triggerOnce, threshold]);

  return {
    ref: viewRef,
  };
};
